<!-- Компактный состав расходов ФАКТа: доли категорий, последние записи и переход к полному списку. -->
<template>
  <section class="fact-expenses">
    <header class="fact-expenses__header">
      <strong>{{ title }}</strong>
      <b v-if="totalLabel" class="fact-expenses__total">{{ totalLabel }}</b>
      <span v-if="subtitle || expenseCountLabel">
        {{ subtitle || expenseCountLabel }}
      </span>
    </header>

    <div
      v-if="showAllLink || $slots.actions"
      class="fact-expenses__actions"
    >
      <BaseActionLink
        v-if="showAllLink"
        class="fact-expenses__link"
        @click="$emit('open-expenses')"
      >
        {{ allExpensesLabel }}
      </BaseActionLink>
      <slot name="actions" />
    </div>

    <template v-if="categories.length">
      <div class="fact-expenses__bar" aria-hidden="true">
        <span
          v-for="(category, index) in categories"
          :key="categoryKey(category)"
          :style="segmentStyle(category, index)"
        ></span>
      </div>

      <div class="fact-expenses__legend">
        <span
          v-for="(category, index) in categories"
          :key="categoryKey(category)"
          class="fact-expenses__legend-item"
        >
          <span
            class="fact-expenses__marker"
            :style="{ backgroundColor: categoryColor(index) }"
          ></span>
          <span v-if="category.emoji" class="fact-expenses__emoji" aria-hidden="true">
            {{ category.emoji }}
          </span>
          {{ category.name || "Без категории" }}
          <strong>{{ formatRub(category.amount) }}</strong>
          <small v-if="hasValue(category.liters)">
            · {{ formatValue(category.liters) }} л
          </small>
        </span>
      </div>
    </template>

    <slot v-else name="empty">
      <p class="fact-expenses__empty">{{ emptyLabel }}</p>
    </slot>

    <div v-if="latestExpenses.length" class="fact-expenses__records">
      <button
        v-for="expense in latestExpenses"
        :key="expense.id"
        type="button"
        class="fact-expenses__record"
        :aria-label="`Открыть расход ${formatRub(expense.amount)}`"
        @click="$emit('edit-expense', expense)"
      >
        <span>{{ formatExpenseDate(expense.spent_at || expense.created_at) }}</span>
        <span class="fact-expenses__record-details">
          <strong class="fact-expenses__record-category">
            <span
              v-if="expense.expense_category?.emoji"
              class="fact-expenses__emoji"
              aria-hidden="true"
            >
              {{ expense.expense_category.emoji }}
            </span>
            <span>{{ expense.expense_category?.name || "Без категории" }}</span>
          </strong>
          <template v-if="hasValue(expense.liters)"> · {{ formatValue(expense.liters) }} л</template>
          <template v-if="expense.comment"> · «{{ expense.comment }}»</template>
          <template v-if="expense.spender?.initials || expense.spender?.name">
            · {{ expense.spender.initials || expense.spender.name }}
          </template>
        </span>
        <span>{{ formatRub(expense.amount) }}</span>
      </button>
    </div>
  </section>
</template>

<script>
import { formatBigNumber } from "@/utils/numberUtils";

const CATEGORY_COLORS = ["#c2410c", "#ea580c", "#fb923c", "#fdba74", "#fed7aa"];

export default {
  name: "OrderPlanCalculationFactExpensesOverview",

  props: {
    title: { type: String, default: "Расходы" },
    subtitle: { type: String, default: "" },
    totalLabel: { type: String, default: "" },
    emptyLabel: { type: String, default: "Расходов по заказу пока нет" },
    categories: { type: Array, default: () => [] },
    latestExpenses: { type: Array, default: () => [] },
    expenseCount: { type: Number, default: 0 },
    expenseCountLabel: { type: String, default: "0 записей" },
    showAllLink: { type: Boolean, default: true },
  },

  emits: ["edit-expense", "open-expenses"],

  computed: {
    allExpensesLabel() {
      return this.expenseCount > this.latestExpenses.length
        ? `Все ${this.expenseCountLabel} ›`
        : "Все расходы ›";
    },
  },

  methods: {
    categoryKey(category) {
      return category.id || category.code || category.name;
    },
    categoryColor(index) {
      return CATEGORY_COLORS[index % CATEGORY_COLORS.length];
    },
    segmentStyle(category, index) {
      return {
        backgroundColor: this.categoryColor(index),
        flexGrow: Math.max(Number(category.amount) || 0, 0),
      };
    },
    formatValue(value) {
      return formatBigNumber(value);
    },
    formatRub(value) {
      return `${formatBigNumber(value)} ₽`;
    },
    formatExpenseDate(value) {
      if (!value) return "—";
      return new Intl.DateTimeFormat("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(value));
    },
    hasValue(value) {
      return value !== null && value !== undefined && value !== "";
    },
  },
};
</script>

<style scoped>
.fact-expenses {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
  padding: 4px 2px 0;
  font-size: 13px;
}

.fact-expenses__header {
  display: flex;
  min-width: 0;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
}

.fact-expenses__header > strong {
  font-weight: 600;
}
.fact-expenses__header > span {
  color: #8b93a7;
  font-size: 12px;
}

.fact-expenses__total {
  color: #1e1e2f;
  font-size: 15px;
  font-variant-numeric: tabular-nums;
}

.fact-expenses__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
}

.fact-expenses__link {
  padding: 0;
  border: 0;
  background: transparent;
  color: #3b5bdb;
  font-family: inherit;
  font-size: 12px;
  cursor: pointer;
}

.fact-expenses__link:hover,
.fact-expenses__link:focus-visible {
  color: #2f49b8;
  outline: none;
  text-decoration: underline;
}

.fact-expenses__bar {
  grid-column: 1 / -1;
  height: 8px;
  display: flex;
  gap: 2px;
  overflow: hidden;
  border-radius: 999px;
  background: #f1f2f6;
}

.fact-expenses__bar > span {
  min-width: 2px;
}

.fact-expenses__legend {
  display: flex;
  grid-column: 1 / -1;
  flex-wrap: wrap;
  gap: 4px 16px;
  color: #4b5068;
  font-size: 12px;
}

.fact-expenses__legend-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.fact-expenses__legend-item strong {
  color: #1e1e2f;
  font-variant-numeric: tabular-nums;
}

.fact-expenses__legend-item small {
  color: #a3a9b8;
  font-size: inherit;
}

.fact-expenses__marker {
  width: 8px;
  height: 8px;
  flex: none;
  border-radius: 2px;
}

.fact-expenses__emoji {
  line-height: 1;
}

.fact-expenses__records {
  display: flex;
  grid-column: 1 / -1;
  flex-direction: column;
  margin-top: 2px;
  border-top: 1px solid #f1f2f6;
}

.fact-expenses__record {
  min-height: 26px;
  display: grid;
  grid-template-columns: minmax(84px, 110px) minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: #8b93a7;
  font-family: inherit;
  font-size: 12px;
  text-align: left;
  cursor: pointer;
}

.fact-expenses__record:hover,
.fact-expenses__record:focus-visible {
  background: #fff7f0;
  outline: none;
}

.fact-expenses__record-details {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fact-expenses__record-category {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #4b5068;
  font-weight: 400;
}
.fact-expenses__record > :last-child {
  color: #4b5068;
  font-variant-numeric: tabular-nums;
  text-align: right;
}

.fact-expenses__empty {
  grid-column: 1 / -1;
  padding: 8px 0;
  color: #8b93a7;
  font-size: 12px;
}

@media (max-width: 760px) {
  .fact-expenses {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 0;
  }

  .fact-expenses__header {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 2px 8px;
  }

  .fact-expenses__header > span {
    grid-column: 1 / -1;
  }

  .fact-expenses__actions {
    display: grid;
    order: 10;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 8px;
  }

  .fact-expenses__actions :deep(.btn),
  .fact-expenses__link {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 44px;
    padding: 0 10px;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
  }

  .fact-expenses__record {
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 2px 12px;
    min-height: 0;
    padding: 10px 0;
    border-top: 1px solid #f3f4f6;
  }

  .fact-expenses__record > :first-child {
    grid-column: 1;
    grid-row: 2;
  }

  .fact-expenses__record-details {
    grid-column: 1;
    grid-row: 1;
  }

  .fact-expenses__record > :last-child {
    grid-column: 2;
    grid-row: 1 / 3;
    align-self: center;
  }
}

</style>
